import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import type {
  ExecutionPhase,
  ReleaseDocumentType,
  ReleaseTestCategory,
  ExecutionStepType,
  OnFailureAction,
  ReleaseApprovalRole,
  ReleasePrerequisiteCategory,
  ValidationCategory,
} from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { EXECUTION_PHASE_LABELS, ON_FAILURE_LABELS } from "../releases/releaseVocabulary";

type ContentKind =
  | "prerequisites" | "approvals" | "execution-steps"
  | "rollback-steps" | "validation-checks"
  | "test-requirements" | "document-requirements";

const PREREQUISITE_CATEGORIES: ReleasePrerequisiteCategory[] = [
  "ACCESS", "INFRASTRUCTURE", "DATABASE", "BACKUP", "ARTIFACT", "CONFIGURATION",
  "SECRETS", "DEPENDENCY", "FEATURE_FLAG", "MONITORING", "MAINTENANCE_WINDOW",
  "COMMUNICATION", "APPROVAL", "SECURITY", "CAPACITY", "EXTERNAL_DEPENDENCY", "OTHER",
];

const APPROVAL_ROLES: ReleaseApprovalRole[] = [
  "QA_LEAD", "TECHNICAL_LEAD", "PROJECT_MANAGER", "RELEASE_MANAGER",
  "DEVOPS", "SECURITY", "BUSINESS_OWNER", "CUSTOMER_UAT", "CHANGE_MANAGER",
];

const STEP_TYPES: ExecutionStepType[] = [
  "MANUAL", "SCRIPT", "PIPELINE", "DATABASE", "CONFIGURATION",
  "INFRASTRUCTURE", "VALIDATION", "COMMUNICATION", "OTHER",
];

const VALIDATION_CATEGORIES: ValidationCategory[] = [
  "APPLICATION_HEALTH", "SMOKE_TEST", "DATABASE", "API", "UI", "QUEUE", "INTEGRATION",
  "MONITORING", "LOGS", "PERFORMANCE", "SECURITY", "BUSINESS_VALIDATION", "CUSTOMER_VALIDATION",
];

const TEST_CATEGORIES: ReleaseTestCategory[] = [
  "SMOKE", "FUNCTIONAL", "REGRESSION", "INTEGRATION", "UAT",
  "PERFORMANCE", "SECURITY", "MIGRATION", "COMPATIBILITY",
];

const DOCUMENT_TYPES: ReleaseDocumentType[] = [
  "RELEASE_NOTES", "DEPLOYMENT_PLAN", "TEST_PLAN", "TEST_REPORT", "UAT_REPORT",
  "ROLLBACK_PLAN", "DATABASE_SCRIPT", "MIGRATION_DOCUMENT", "ARCHITECTURE_DOCUMENT",
  "RUNBOOK", "OPERATIONAL_GUIDE", "APPROVAL_DOCUMENT", "CHANGE_REQUEST", "CAB_APPROVAL",
  "CUSTOMER_APPROVAL", "USER_GUIDE", "TRAINING_DOCUMENT", "KNOWN_ISSUES",
];

const TITLES: Record<ContentKind, string> = {
  "prerequisites": "Add a prerequisite",
  "approvals": "Add an approval",
  "execution-steps": "Add a runbook step",
  "rollback-steps": "Add a rollback step",
  "validation-checks": "Add a post-deployment check",
  "test-requirements": "Require a kind of testing",
  "document-requirements": "Require a document",
};

/**
 * Adding one item to a draft template version.
 *
 * <p>One drawer for all five kinds rather than five near-identical ones. They share a shape —
 * a title, whether it is mandatory, and a kind-specific field or two — and five files would drift
 * on the parts that are the same.
 *
 * <p>Instruction fields carry §33's warning. It matters more here than on a release: a credential
 * pasted into a template is copied into every release ever built from it, and goes on being
 * copied long after the secret itself has been rotated.
 */
export function TemplateContentDrawer({
  versionId,
  kind,
  onClose,
  onSaved,
}: {
  versionId: string | null;
  kind: ContentKind | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [instruction, setInstruction] = useState("");
  const [mandatory, setMandatory] = useState(true);
  const [evidenceRequired, setEvidenceRequired] = useState(false);
  const [category, setCategory] = useState("BACKUP");
  const [approvalRole, setApprovalRole] = useState<ReleaseApprovalRole>("QA_LEAD");
  const [phase, setPhase] = useState<ExecutionPhase>("DEPLOY");
  const [stepType, setStepType] = useState<ExecutionStepType>("MANUAL");
  const [onFailure, setOnFailure] = useState<OnFailureAction>("STOP_RELEASE");
  const [testCategory, setTestCategory] = useState<ReleaseTestCategory>("REGRESSION");
  const [documentType, setDocumentType] = useState<ReleaseDocumentType>("RELEASE_NOTES");
  const [minutes, setMinutes] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (kind == null) {
      return;
    }
    setTitle("");
    setDescription("");
    setInstruction("");
    setMandatory(true);
    setEvidenceRequired(false);
    setCategory(kind === "validation-checks" ? "APPLICATION_HEALTH" : "BACKUP");
    setApprovalRole("QA_LEAD");
    setPhase("DEPLOY");
    setStepType("MANUAL");
    setOnFailure("STOP_RELEASE");
    setMinutes("");
    setError(null);
  }, [kind]);

  if (kind == null || versionId == null) {
    return null;
  }

  const bodyFor = (): unknown => {
    switch (kind) {
      case "prerequisites":
        return {
          category, title: title.trim(),
          description: description.trim() === "" ? null : description.trim(),
          mandatory, evidenceRequired, validationMethod: null,
        };
      case "approvals":
        return { approvalRole, mandatory };
      case "execution-steps":
        return {
          phase, title: title.trim(),
          instruction: instruction.trim() === "" ? null : instruction.trim(),
          stepType, component: null, mandatory, evidenceRequired,
          onFailureAction: onFailure,
          plannedDurationMinutes: minutes === "" ? null : Number(minutes),
        };
      case "rollback-steps":
        return {
          title: title.trim(),
          instruction: instruction.trim() === "" ? null : instruction.trim(),
          estimatedMinutes: minutes === "" ? null : Number(minutes),
        };
      case "validation-checks":
        return {
          category, checkName: title.trim(),
          description: description.trim() === "" ? null : description.trim(),
          mandatory,
        };
      case "test-requirements":
        return { category: testCategory, required: mandatory };
      case "document-requirements":
        return { documentType, mandatory };
      default:
        return {};
    }
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/release-template-versions/${versionId}/${kind}`, bodyFor());
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not add that.");
    } finally {
      setSaving(false);
    }
  };

  const needsTitle =
    kind !== "approvals" && kind !== "test-requirements" && kind !== "document-requirements";
  const canSubmit = !needsTitle || title.trim() !== "";

  return (
    <Drawer
      open
      onClose={onClose}
      title={TITLES[kind]}
      subtitle="Every release built from this version will start with it."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit} loading={saving}>
            {saving ? "Adding…" : "Add"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {kind === "test-requirements" ? (
          <Field label="Which kind of testing" required>
            <Select
              value={testCategory}
              onChange={(event) => setTestCategory(event.target.value as ReleaseTestCategory)}
            >
              {TEST_CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {value.replace(/_/g, " ").toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        ) : kind === "document-requirements" ? (
          <Field label="Which document" required>
            <Select
              value={documentType}
              onChange={(event) => setDocumentType(event.target.value as ReleaseDocumentType)}
            >
              {DOCUMENT_TYPES.map((value) => (
                <option key={value} value={value}>
                  {value.replace(/_/g, " ").toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        ) : kind === "approvals" ? (
          <Field label="Who has to sign off" required>
            <Select
              value={approvalRole}
              onChange={(event) => setApprovalRole(event.target.value as ReleaseApprovalRole)}
            >
              {APPROVAL_ROLES.map((role) => (
                <option key={role} value={role}>
                  {role.replace(/_/g, " ").toLowerCase()}
                </option>
              ))}
            </Select>
          </Field>
        ) : (
          <Field label={kind === "validation-checks" ? "What is checked" : "What is done"} required>
            <Input
              autoFocus
              value={title}
              placeholder={
                kind === "prerequisites"
                  ? "Production DB snapshot taken"
                  : kind === "execution-steps"
                    ? "Take a database snapshot"
                    : kind === "rollback-steps"
                      ? "Restore backend image"
                      : "Sign-in smoke test"
              }
              onChange={(event) => setTitle(event.target.value)}
            />
          </Field>
        )}

        {(kind === "prerequisites" || kind === "validation-checks") && (
          <Field label="Category" required>
            <Select value={category} onChange={(event) => setCategory(event.target.value)}>
              {(kind === "prerequisites" ? PREREQUISITE_CATEGORIES : VALIDATION_CATEGORIES)
                .map((value) => (
                  <option key={value} value={value}>
                    {value.replace(/_/g, " ").toLowerCase()}
                  </option>
                ))}
            </Select>
          </Field>
        )}

        {kind === "execution-steps" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Phase" required>
              <Select
                value={phase}
                onChange={(event) => setPhase(event.target.value as ExecutionPhase)}
              >
                {Object.entries(EXECUTION_PHASE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Kind" required>
              <Select
                value={stepType}
                onChange={(event) => setStepType(event.target.value as ExecutionStepType)}
              >
                {STEP_TYPES.map((value) => (
                  <option key={value} value={value}>
                    {value.toLowerCase()}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        )}

        {(kind === "execution-steps" || kind === "rollback-steps") && (
          <Field
            label="Instructions"
            hint="Never a password, key or token — reference where the secret lives instead."
          >
            <Textarea
              rows={3}
              value={instruction}
              placeholder="pg_dump against the primary — use secret prod/db from Vault."
              onChange={(event) => setInstruction(event.target.value)}
            />
          </Field>
        )}

        {(kind === "prerequisites" || kind === "validation-checks") && (
          <Field label="Detail" hint="Enough that somebody else could do it.">
            <Textarea
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
        )}

        {(kind === "execution-steps" || kind === "rollback-steps") && (
          <Field label="Expected minutes">
            <Input
              type="number"
              min={0}
              value={minutes}
              onChange={(event) => setMinutes(event.target.value)}
            />
          </Field>
        )}

        {kind === "execution-steps" && (
          <Field
            label="If it fails"
            hint="Recorded as what should happen. Nothing is triggered automatically."
          >
            <Select
              value={onFailure}
              onChange={(event) => setOnFailure(event.target.value as OnFailureAction)}
            >
              {Object.entries(ON_FAILURE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        )}

        {kind !== "rollback-steps" && (
          <label className="flex items-center gap-2 text-sm text-ink-800">
            <input
              type="checkbox"
              checked={mandatory}
              onChange={(event) => setMandatory(event.target.checked)}
            />
            Mandatory
          </label>
        )}

        {(kind === "prerequisites" || kind === "execution-steps") && (
          <label className="flex items-center gap-2 text-sm text-ink-800">
            <input
              type="checkbox"
              checked={evidenceRequired}
              onChange={(event) => setEvidenceRequired(event.target.checked)}
            />
            Requires evidence before it can be completed
          </label>
        )}
      </div>
    </Drawer>
  );
}
